/* eslint-disable @typescript-eslint/naming-convention */

import {
    ButtonTitles,
    ExtraItem,
    ExtraItemData,
    ExtraItemTypes,
    IUpdateItem,
    PlanDetails,
    RenderingDetails,
    RenderingDetailsMeta,
    SelectedOption,
} from "@swiggy-private/cart-types";
import { isEmpty } from "@swiggy-private/core-ui";

import { PLACEHOLDERS } from "@cart-ui/constants/OfferBundle";
import { RENDERING_DETAILS_TYPES, VALID_RENDERING_DETAILS_TYPE } from "@cart-ui/constants/RenderingDetails";
import { IFooterTitleInfo } from "@cart-ui/components/offer-pack-nudge-widget/types";
import { ProgramSubTypes, ProgramTypes } from "@swiggy-private/super-ui";

type IExtraItemInfo = {
    name?: string;
    subText?: string;
};

const hasSubtitleAndDelFee = (item: ExtraItemData) =>
    Boolean(item?.line_item_comms?.sub_title?.length && item?.line_item_comms?.attribute_values?.del_fee);

const subtitleHasPlaceholder = (item: ExtraItemData) =>
    item?.line_item_comms?.sub_title?.[0].indexOf(PLACEHOLDERS.DELIVERY_FEE) !== -1;

const subtitle = (item: ExtraItemData) => item?.line_item_comms?.sub_title?.[0];

const shouldFormatSubtext = (item: ExtraItemData) => hasSubtitleAndDelFee(item) && subtitleHasPlaceholder(item);

export const getOfferBundleInfo = (item?: ExtraItemData, styles?: Record<string, string>): IExtraItemInfo | null => {
    if (!item) return null;

    let subText = subtitle(item);

    if (shouldFormatSubtext(item)) {
        subText = subtitle(item).replace(
            PLACEHOLDERS.DELIVERY_FEE,
            `<span class=${styles?.strikethrough}>${item.line_item_comms.attribute_values.del_fee}</span>`,
        );
    }

    return {
        name: item.line_item_comms?.title,
        subText,
    };
};

export const isExtraItemDataValid = (item?: ExtraItem) => Boolean(item && !isEmpty(item?.type) && !isEmpty(item?.data));

export const isAutoAddOrOneTapType = (item?: ExtraItem) =>
    item?.type &&
    [ExtraItemTypes.NUDGE_TYPE_AUTO_ADD, ExtraItemTypes.NUDGE_TYPE_ONE_TAP].includes(item.type as ExtraItemTypes);

export const getButtonTitle = (item?: IUpdateItem, type?: string, isOfferPackAdded?: boolean) => {
    if (
        item?.nudge_type === ExtraItemTypes.NUDGE_TYPE_AUTO_ADD ||
        type?.toUpperCase() === VALID_RENDERING_DETAILS_TYPE.OFFER_BUNDLE_BENEFITS ||
        isOfferPackAdded
    )
        return ButtonTitles.REMOVE;

    return ButtonTitles.ADD;
};

export const getPlanForAnalytics = (selectedOption: SelectedOption, planDetails?: PlanDetails, onePlanType?: string) =>
    JSON.stringify(
        isEmpty(onePlanType)
            ? {
                  offer_bundle: true,
                  bundle_reject: selectedOption === SelectedOption.REMOVE,
                  plan_id: planDetails?.plan_id,
              }
            : {
                  auto_add: true,
                  bundle_reject: selectedOption === SelectedOption.REMOVE,
                  plan_id: planDetails?.plan_id,
                  one_type: onePlanType,
              },
    );

export const isActive = (item?: RenderingDetailsMeta) => item && !item.nudge_type;

export const getPlanForNudgeAnalytics = (item?: IUpdateItem, type?: string, onePlanType?: string) => {
    const buttonTitle = getButtonTitle(item, type);

    return isEmpty(onePlanType)
        ? JSON.stringify({
              offer_bundle_added: buttonTitle === ButtonTitles.REMOVE,
              plan_id: item?.plan_details?.plan_id,
          })
        : JSON.stringify({
              auto_add_added: buttonTitle === ButtonTitles.REMOVE,
              plan_id: item?.plan_details?.plan_id,
              one_type: onePlanType,
          });
};

export const getActiveFooterTitleInfo = (item: RenderingDetailsMeta): IFooterTitleInfo => ({
    title: item?.benefits?.[0].title || "",
    variant: "V2.body3.neutral.medium",
    color: "text_Med_Emphasis",
});

export const getNudgeFooterTitleInfo = (item?: RenderingDetailsMeta): IFooterTitleInfo => ({
    title: item?.plan_details?.name,
    variant: "V2.body2.neutral.bold",
});

export const createBenefits = (item: RenderingDetailsMeta) =>
    item.subtitles?.map((subtitle: string) => ({
        title: subtitle,
    }));

export const getContextForActiveAnalytics = (item?: RenderingDetailsMeta) =>
    JSON.stringify({
        savings_text: item?.subtitles?.[0],
        validity_text: item?.benefits?.[0]?.title,
    });

export const getIsOfferPackAdded = (renderingDetails?: RenderingDetails[]) => {
    const billDetails = renderingDetails?.find(o => o.key.toUpperCase() === RENDERING_DETAILS_TYPES.BILL_HEADING);
    const isOfferPackAdded = billDetails?.children?.filter(o =>
        [
            RENDERING_DETAILS_TYPES.POSITIVE_DELIVERY_CHARGES_EXCLUSIVE,
            RENDERING_DETAILS_TYPES.OFFER_BUNDLE_TOTAL,
        ].includes(o.key.toUpperCase()),
    );

    return Boolean(isOfferPackAdded && isOfferPackAdded.length === 2);
};

export const isSwiggyOnePlanVariants = (planDetails: PlanDetails) =>
    planDetails?.program === ProgramTypes.ONE &&
    (planDetails?.type === ProgramSubTypes.PLUS || planDetails?.type === ProgramSubTypes.LITE);

export const isSwiggyOneLitePlan = (planType: string) => {
    return planType === ProgramSubTypes.LITE;
};
